import { useMutation, useQuery } from '@apollo/client';
import {
    Alert,
    Button,
    Drawer,
    Space,
    Spin,
    Tabs,
    Tag,
    Typography,
    message
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { formatTimestampToDateTime } from 'utils/dateTimeUtils';
import { formatWeightKgToLb } from 'utils/numberUtils';
import { GET_BAG_DETAIL_PAGED, GetBagDetailPagedResponse } from '../../../graphql/queries/getBagDetail';
import { GET_ALL_CARRIER_OPTIONS, GetAllCarrierOptionsResponse } from './graphql/queries/getAllCarrierOptions';
import { getCarrierName } from './utils/carrierUtils';
import { BagPrintItem, PRINT_SELECTED_TAGS, PrintSelectedTagsResponse } from './graphql/mutations/batchOperations';
import { printPdfFromBase64 } from './utils/printUtils';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from "../../../contexts/WarehouseContext";
import BagShipmentTimeline from './components/BagShipmentTimeline';
import BagShipmentSnapshotPanel from './components/BagShipmentSnapshotPanel';
import {
    GET_BAG_SHIPMENT_HISTORY,
    type GetBagShipmentHistoryResponse,
    type GetBagShipmentHistoryVariables,
} from './graphql/queries/getBagShipmentHistory';
import { getBagSourceTypeText, getExternalBusinessTypeText } from './graphql/types/BagTagType';
import { BagBatchSegmentDetail, BagBatchSegmentList } from './components/BagBatchRecords';
import {
    GET_BAG_BATCH_RECORDS,
    type GetBagBatchRecordsResponse,
    type GetBagBatchRecordsVariables,
} from './graphql/queries/getBagBatchRecords';


const { Text } = Typography;

/** 左侧 Tab：包牌轨迹（发运历史时间轴 + 右侧运单快照）/ 批次记录（批次段 + 右侧该段运单） */
type BagDetailTab = 'track' | 'batch';

interface BagDetailDrawerProps {
    visible: boolean;
    bagNumber: string | null;
    onClose: () => void;
}

// Drawer 目标宽度 1200px；窗口不足时按 95% viewport 自适应，避免小屏溢出
const useDrawerWidth = (preferred = 1200, ratio = 0.95) => {
    const [width, setWidth] = useState(() =>
        typeof window === 'undefined' ? preferred : Math.min(preferred, Math.floor(window.innerWidth * ratio))
    );
    useEffect(() => {
        const onResize = () => setWidth(Math.min(preferred, Math.floor(window.innerWidth * ratio)));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [preferred, ratio]);
    return width;
};

const BagDetailDrawer: React.FC<BagDetailDrawerProps> = ({ visible, bagNumber, onClose }) => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<BagDetailTab>('track');
    const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number | null>(null);
    const drawerWidth = useDrawerWidth();

    const { data, loading, error, refetch } = useQuery<GetBagDetailPagedResponse>(GET_BAG_DETAIL_PAGED, {
        variables: { bagNumber },
        skip: !bagNumber || !visible,
        errorPolicy: 'all'
    });

    const {
        data: historyData,
        loading: historyLoading,
    } = useQuery<GetBagShipmentHistoryResponse, GetBagShipmentHistoryVariables>(
        GET_BAG_SHIPMENT_HISTORY,
        {
            variables: { bagNumber: bagNumber ?? '' },
            skip: !bagNumber || !visible,
            errorPolicy: 'all',
        }
    );

    const { data: batchRecordData, loading: batchRecordsLoading, error: batchRecordsError } = useQuery<GetBagBatchRecordsResponse, GetBagBatchRecordsVariables>(
        GET_BAG_BATCH_RECORDS,
        {
            variables: { bagNumber: bagNumber ?? '' },
            skip: !bagNumber || !visible,
            errorPolicy: 'all',
        }
    );

    const [printSelectedTags, { loading: printing }] = useMutation<PrintSelectedTagsResponse>(PRINT_SELECTED_TAGS);

    const { data: carrierData } = useQuery<GetAllCarrierOptionsResponse>(GET_ALL_CARRIER_OPTIONS);
    const carrierOptions = carrierData?.getAllCarrierOptions || [];

    const bagDetail = data?.bagDetail;
    const shipmentNodes = useMemo(() => historyData?.bagShipmentHistory ?? [], [historyData]);
    const batchRecords = useMemo(() => batchRecordData?.bagBatchRecords ?? [], [batchRecordData]);
    // 「当前所属批次」按操作仓隔离：只取本仓未移出(leftAt===null)的段。跨仓整包移入时同一包牌在多个仓各有一条
    // leftAt 为 null 的段，不按仓过滤会把他仓批次也列出来；无操作仓上下文时退回不按仓（防御，正常列表/详情恒有仓）。
    const currentBatchSegments = batchRecords
        .filter((r) => r.leftAt === null && (!currentWarehouse?.id || String(r.warehouseId) === String(currentWarehouse.id)));
    const currentBatchNames = currentBatchSegments.map((r) => r.batchName);
    // 打印时透传当前所属批次 id（= 上面展示的那个批次），标签批次段与详情显示一致，跨仓也一致
    const currentBatchId = currentBatchSegments[0]?.batchId;
    // 批次记录还没拿到（加载中或失败）时，不下「不属于任何批次」「0 段」这类结论
    const isBatchRecordsPending = !batchRecordData && (batchRecordsLoading || !!batchRecordsError);
    // 默认选中最近加入的批次段
    const segmentIndex = Math.min(selectedSegmentIndex ?? batchRecords.length - 1, batchRecords.length - 1);

    const renderBatchSegments = () => {
        if (isBatchRecordsPending) {
            return batchRecordsLoading
                ? <div className="py-6 text-center"><Spin /></div>
                : <Text type="danger">{t('wms.sortingBatch.common.requestFailed')}</Text>;
        }
        return <BagBatchSegmentList records={batchRecords} selectedIndex={segmentIndex} onSelect={setSelectedSegmentIndex} />;
    };

    // 换包牌时回到「包牌轨迹」并重置批次段选择
    useEffect(() => {
        setActiveTab('track');
        setSelectedSegmentIndex(null);
    }, [bagNumber]);

    useEffect(() => {
        if (!shipmentNodes.length) {
            setSelectedNodeId(null);
            return;
        }
        const currentNode = shipmentNodes.find((n) => n.kind === 'CURRENT');
        setSelectedNodeId((currentNode ?? shipmentNodes[shipmentNodes.length - 1]).id);
    }, [shipmentNodes]);

    const getBagStatusText = (status: number) => {
        const statusMap: Record<number, { text: string; color: string }> = {
            0: { text: t('enum.bagStatus.notUsed'), color: 'blue' },
            1: { text: t('enum.bagStatus.used'), color: 'green' },
            2: { text: t('enum.bagStatus.discarded'), color: 'grey' },
            3: { text: t('enum.bagStatus.handover'), color: 'orange' },
            4: { text: t('enum.bagStatus.closed'), color: 'red' }
        };
        return statusMap[status] || { text: t('enum.bagStatus.unknown'), color: 'default' };
    };

    const handlePrint = async () => {
        if (!bagDetail?.id) {
            message.error(t('wms.bags.messages.bagInfoIncomplete'));
            return;
        }

        const items: BagPrintItem[] = [{
            bagId: bagDetail.id.toString(),
            copies: 1,
            batchId: currentBatchId,
        }];

        try {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const localTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const result = await printSelectedTags({
                variables: {
                    items,
                    localTime,
                    timeZone
                }
            });
            if (result.data?.printSelectedTags?.success && result.data.printSelectedTags.pdfBase64) {
                printPdfFromBase64(result.data.printSelectedTags.pdfBase64, `bag_tag_${bagDetail.bagNumber}_${Date.now()}.pdf`);
                message.success(t('wms.bags.messages.bagLabelGeneratedSuccessfully'));
            } else {
                message.error(result.data?.printSelectedTags?.message || t('wms.bags.messages.bagLabelGeneratedFailed'));
            }
        } catch (error) {
            console.error('打印包牌失败:', error);
            message.error('打印包牌时发生错误');
        }
    };

    const renderContent = () => {
        if (!visible) {
            return null;
        }

        if (loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <Spin size="large" />
                </div>
            );
        }

        if (error || !bagDetail) {
            return (
                <Alert
                    message={t('wms.bags.messages.loadFailed')}
                    description={error?.message || t('wms.bags.messages.bagInfoNotFound')}
                    type="error"
                    showIcon
                    action={
                        <Space>
                            <Button size="small" onClick={() => refetch()}>{t('components.button.retry')}</Button>
                            <Button size="small" onClick={onClose}>{t('components.button.close')}</Button>
                        </Space>
                    }
                />
            );
        }

        const statusInfo = getBagStatusText(bagDetail.status);

        return (
            <div style={{ padding: '5px 24px', display: 'flex', gap: 16 }}>
                {/* 左侧：基本信息 + 发运历史时间轴 */}
                <div style={{ flex: '0 0 360px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.creationHub')}</Text>
                                <Text>{bagDetail.warehouse?.name || 'Unknown'}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.columns.creationType')}</Text>
                                <Text>{getBagSourceTypeText(bagDetail.sourceType, t)}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.createTime')}</Text>
                                <Text>{formatTimestampToDateTime(bagDetail.createdAt)}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.bagNum')}</Text>
                                <Text>{bagDetail.bagNumber}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.sortingBatch.bagDetail.attrs.currentBatches')}</Text>
                                {isBatchRecordsPending
                                    ? <Text type="secondary">-</Text>
                                    : currentBatchNames.length
                                        ? <Text className="text-ant-purple-6">{currentBatchNames.join(String(t('wms.sortingBatch.common.separator')))}</Text>
                                        : <Text type="secondary">{t('wms.sortingBatch.common.notInAnyBatch')}</Text>}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.bagSortingCode')}</Text>
                                <Text>{bagDetail.sortingCode || '-'}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.bagStatus')}</Text>
                                <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.carrier')}</Text>
                                <Text>{getCarrierName(bagDetail.carrierCode, carrierOptions)}</Text>
                            </div>
                            {/* 装包重量：后端存 bag.weight_kg（订单累计，不含皮重）+ containerTareWeightKg（容器皮重，未绑定容器为 null）；
                                页面展示时相加得到"包裹 + 容器"总重量，再转换为 lb（工具已 i18n"lb/磅"）。
                                未称重（weightKg null/0）时不加皮重，由 formatWeightKgToLb 内部兜底为 '-'。
                                与 BagsPage.tsx 列表页 packageWeight 列口径完全一致。 */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">
                                    {t('wms.bags.pageDetails.packageWeight')}
                                </Text>
                                <Text>{(() => {
                                    const orderKg = bagDetail.weightKg ?? 0;
                                    if (orderKg <= 0) return formatWeightKgToLb(bagDetail.weightKg);
                                    return formatWeightKgToLb(orderKg + (bagDetail.containerTareWeightKg ?? 0));
                                })()}</Text>
                            </div>
                            {/* PPMOD 相关：外部业务类型 + 容器类型（含名字快照）；无值展示 '-' */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">
                                    {t('wms.bags.pageDetails.externalBusinessType') || t('wms.bags.columns.externalBusinessType') || 'External Business Type'}
                                </Text>
                                <Text>{getExternalBusinessTypeText(bagDetail.externalBusinessType, t)}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">
                                    {t('wms.bags.pageDetails.containerType') || t('wms.bags.columns.containerType') || 'Container Type'}
                                </Text>
                                <Text>{bagDetail.containerName || '-'}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.ttBizType')}</Text>
                                <Text>{bagDetail.ttBizType === 'UNLIMITED' ? t('wms.bags.pageDetails.ttBizTypeUnlimited') : (bagDetail.ttBizType || '-')}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.sortingSegmentCode')}</Text>
                                <Text>{bagDetail.sortingSegmentCode || '-'}</Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="secondary" className="min-w-20">{t('wms.bags.pageDetails.lastMileSortingCode')}</Text>
                                <Text>{bagDetail.lastMileSortingCode || '-'}</Text>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Tabs
                            activeKey={activeTab}
                            onChange={(key) => setActiveTab(key as BagDetailTab)}
                            items={[
                                { key: 'track', label: t('wms.sortingBatch.bagDetail.tabs.track') },
                                {
                                    key: 'batch',
                                    label: isBatchRecordsPending
                                        ? t('wms.sortingBatch.bagDetail.segment.title')
                                        : t('wms.sortingBatch.bagDetail.tabs.batch', { count: batchRecords.length }),
                                },
                            ]}
                        />
                        {activeTab === 'track' ? (
                            <BagShipmentTimeline
                                nodes={shipmentNodes}
                                selectedNodeId={selectedNodeId}
                                onSelect={setSelectedNodeId}
                                loading={historyLoading}
                            />
                        ) : renderBatchSegments()}
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            type="primary"
                            onClick={handlePrint}
                            loading={printing}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('wms.bags.pageDetails.printBagLabel')}</span>
                        </Button>
                        <Button
                            onClick={onClose}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">OK</span>
                        </Button>
                    </div>
                </div>

                {/* 右侧：包牌轨迹 Tab 为选中节点的运单快照；批次记录 Tab 为选中批次段的运单 */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* 切到批次记录只隐藏不卸载：快照面板的页码、筛选、每页条数存在面板内部，卸载就丢了 */}
                    {bagNumber && (
                        <div style={{ display: activeTab === 'track' ? undefined : 'none' }}>
                            <BagShipmentSnapshotPanel
                                bagNumber={bagNumber}
                                nodes={shipmentNodes}
                                selectedNodeId={selectedNodeId}
                                onSelectNode={setSelectedNodeId}
                            />
                        </div>
                    )}
                    {activeTab === 'batch' && <BagBatchSegmentDetail record={batchRecords[segmentIndex]} />}
                </div>
            </div>
        );
    };

    return (
        <Drawer
            title={`${t('wms.bags.pageDetails.bagDetail')} - ${bagNumber || ''}`}
            placement="right"
            onClose={onClose}
            open={visible}
            width={drawerWidth}
        >
            {renderContent()}
        </Drawer>
    );
};

export default BagDetailDrawer;
